import React, { useRef, useState } from 'react';
import {
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { Clear, CloudUpload, EditOutlined } from '@mui/icons-material';
import { dateFormat } from '../../utils';
import SubMatTemplateSendSection from './SubMatTemplateSendSection';
import {
  ColumnDataCell,
  ColumnHeaderCell,
  ColumnRow,
  useColumnWidths,
} from '../G04docuSubMatFolder/SubMatFolderColumnLayout';

const PDF_ACCEPT = '.pdf,application/pdf';
const COL_MIN = [120, 100, 64, 56, 56];
const COL_DEFAULT = [180, 130, 72, 64, 64];
export const DEFAULT_EXTRA_SECTION_TITLE = '별도 첨부';

const formatFileSize = (bytes) => {
  if (bytes == null || bytes === '') return '';
  const n = Number(bytes);
  if (Number.isNaN(n) || n < 0) return '';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(n < 10240 ? 1 : 0)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
};

/**
 * 별도 첨부 — 부자재등록 탐색기(중간 컬럼)형 목록 + D&D 순서
 * title / sectionTitle 은 목차·합본 표시용 (실제 파일명 변경 아님)
 */
const SubMatTemplateExtraAttachmentsList = ({
  extras,
  onChange,
  onAddFiles,
  sectionTitle = DEFAULT_EXTRA_SECTION_TITLE,
  onSectionTitleChange,
}) => {
  const { widths, startResize } = useColumnWidths(COL_DEFAULT, COL_MIN);
  const gridTemplateColumns = widths.map((w) => `${w}px`).join(' ');
  const dragIdRef = useRef(null);
  const [dragId, setDragId] = useState(null);
  const [dropHint, setDropHint] = useState(null); // { targetId, position: 'before'|'after' }
  const [dragOverPane, setDragOverPane] = useState(false);
  const [rowMenu, setRowMenu] = useState(null); // { mouseX, mouseY, item }
  const [renameTarget, setRenameTarget] = useState(null); // { type: 'section'|'row', id?, value }

  const displaySectionTitle = (sectionTitle || '').trim() || DEFAULT_EXTRA_SECTION_TITLE;

  const applyReorder = (sourceId, targetId, position) => {
    if (!sourceId || !targetId || sourceId === targetId) return;
    const from = extras.findIndex((e) => e.id === sourceId);
    if (from < 0) return;
    const next = [...extras];
    const [row] = next.splice(from, 1);
    let to = next.findIndex((e) => e.id === targetId);
    if (to < 0) return;
    const insertAt = position === 'after' ? to + 1 : to;
    next.splice(insertAt, 0, row);
    onChange(next);
  };

  const resolvePosition = (e, el) => {
    const rect = el.getBoundingClientRect();
    const ratio = (e.clientY - rect.top) / Math.max(rect.height, 1);
    return ratio < 0.5 ? 'before' : 'after';
  };

  const handlePaneDropFiles = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverPane(false);
    const files = [...(e.dataTransfer.files || [])];
    if (files.length) onAddFiles?.(files);
  };

  const commitRename = () => {
    if (!renameTarget) return;
    const next = (renameTarget.value || '').trim();
    if (renameTarget.type === 'section') {
      onSectionTitleChange?.(next || DEFAULT_EXTRA_SECTION_TITLE);
    } else if (renameTarget.type === 'row' && renameTarget.id) {
      const row = extras.find((e) => e.id === renameTarget.id);
      const fallback = (row?.file?.name || '첨부').replace(/\.pdf$/i, '');
      onChange(extras.map((e) => (
        e.id === renameTarget.id ? { ...e, title: next || fallback } : e
      )));
    }
    setRenameTarget(null);
  };

  const uploadAction = (
    <Tooltip title="PDF 추가">
      <IconButton size="small" component="label" aria-label="PDF 추가">
        <CloudUpload fontSize="small" />
        <input
          type="file"
          hidden
          accept={PDF_ACCEPT}
          multiple
          onChange={(e) => {
            const files = [...(e.target.files || [])];
            e.target.value = '';
            if (files.length) onAddFiles?.(files);
          }}
        />
      </IconButton>
    </Tooltip>
  );

  return (
    <Box
      onDragOver={(e) => {
        e.preventDefault();
        if ([...(e.dataTransfer.types || [])].includes('Files')) setDragOverPane(true);
      }}
      onDragLeave={() => setDragOverPane(false)}
      onDrop={handlePaneDropFiles}
      sx={{
        outline: dragOverPane ? '2px dashed' : 'none',
        outlineColor: 'primary.main',
        borderRadius: 1,
        bgcolor: dragOverPane ? 'rgba(25, 118, 210, 0.08)' : 'transparent',
      }}
    >
      <SubMatTemplateSendSection
        title={(
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, minWidth: 0 }}>
            <Typography variant="subtitle2" component="span" noWrap>
              {displaySectionTitle}
            </Typography>
            <Tooltip title="섹션 표시명 변경">
              <IconButton
                size="small"
                aria-label="섹션 표시명 변경"
                onClick={() => setRenameTarget({ type: 'section', value: displaySectionTitle })}
                sx={{ p: 0.25 }}
              >
                <EditOutlined sx={{ fontSize: 16 }} />
              </IconButton>
            </Tooltip>
          </Box>
        )}
        action={uploadAction}
        sx={{ minHeight: 160 }}
      >
        <ColumnRow header gridTemplateColumns={gridTemplateColumns}>
          <ColumnHeaderCell label="이름" resizable onResizeStart={(x) => startResize(0, x)} />
          <ColumnHeaderCell label="수정한 날짜" resizable onResizeStart={(x) => startResize(1, x)} />
          <ColumnHeaderCell label="유형" resizable onResizeStart={(x) => startResize(2, x)} />
          <ColumnHeaderCell label="크기" align="right" resizable onResizeStart={(x) => startResize(3, x)} />
          <ColumnHeaderCell label="기능" align="center" resizable onResizeStart={(x) => startResize(4, x)} />
        </ColumnRow>

        {extras.length === 0 ? (
          <Box sx={{ p: 2 }}>
            <Typography
              variant="body2"
              color={dragOverPane ? 'primary' : 'text.secondary'}
              sx={{ opacity: dragOverPane ? 1 : 0.55, fontWeight: dragOverPane ? 600 : 400 }}
            >
              PDF를 여기에 놓거나 업로드 아이콘으로 추가하세요.
            </Typography>
          </Box>
        ) : (
          extras.map((ex) => {
            const isDragging = dragId === ex.id;
            const hint = dropHint?.targetId === ex.id ? dropHint.position : null;
            const modified = ex.file?.lastModified
              ? dateFormat(new Date(ex.file.lastModified))
              : '';
            return (
              <ColumnRow
                key={ex.id}
                draggable
                gridTemplateColumns={gridTemplateColumns}
                onDragStart={(e) => {
                  e.dataTransfer.effectAllowed = 'move';
                  e.dataTransfer.setData('text/plain', ex.id);
                  dragIdRef.current = ex.id;
                  setDragId(ex.id);
                }}
                onDragEnd={() => {
                  dragIdRef.current = null;
                  setDragId(null);
                  setDropHint(null);
                }}
                onDragOver={(e) => {
                  e.preventDefault();
                  e.dataTransfer.dropEffect = 'move';
                  setDropHint({ targetId: ex.id, position: resolvePosition(e, e.currentTarget) });
                }}
                onDrop={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  const id = e.dataTransfer.getData('text/plain') || dragIdRef.current;
                  applyReorder(id, ex.id, resolvePosition(e, e.currentTarget));
                  setDragId(null);
                  setDropHint(null);
                }}
                onContextMenu={(e) => {
                  e.preventDefault();
                  setRowMenu({ mouseX: e.clientX, mouseY: e.clientY, item: ex });
                }}
                onDoubleClick={() => {
                  setRenameTarget({
                    type: 'row',
                    id: ex.id,
                    value: ex.title || (ex.file?.name || '').replace(/\.pdf$/i, '') || '',
                  });
                }}
                sx={{
                  py: 0.35,
                  minHeight: 32,
                  cursor: 'grab',
                  opacity: isDragging ? 0.35 : (ex.included === false ? 0.5 : 1),
                  bgcolor: 'transparent',
                  boxShadow: hint === 'before'
                    ? (theme) => `inset 0 2px 0 ${theme.palette.primary.main}`
                    : hint === 'after'
                      ? (theme) => `inset 0 -2px 0 ${theme.palette.primary.main}`
                      : 'none',
                  '&:hover': { bgcolor: 'action.hover' },
                  '&:active': { cursor: 'grabbing' },
                  fontSize: '0.9375rem',
                  lineHeight: 1.45,
                }}
              >
                <Box
                  sx={{
                    minWidth: 0,
                    width: '100%',
                    px: 1,
                    boxSizing: 'border-box',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    overflow: 'hidden',
                  }}
                >
                  <Checkbox
                    size="small"
                    checked={ex.included !== false}
                    onChange={(e) => {
                      onChange(extras.map((row) => (
                        row.id === ex.id ? { ...row, included: e.target.checked } : row
                      )));
                    }}
                    onClick={(e) => e.stopPropagation()}
                    onMouseDown={(e) => e.stopPropagation()}
                    sx={{ p: 0.25 }}
                  />
                  <Box component="span" sx={{ flexShrink: 0, lineHeight: 1.45, fontSize: '1.05em' }}>📄</Box>
                  <Box
                    component="span"
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                    title={`${ex.title || ex.file?.name || '첨부'} (실제 파일: ${ex.file?.name || '-'})`}
                  >
                    {ex.title || ex.file?.name || '첨부'}
                  </Box>
                </Box>
                <ColumnDataCell>{modified}</ColumnDataCell>
                <ColumnDataCell>PDF 파일</ColumnDataCell>
                <ColumnDataCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                  {formatFileSize(ex.file?.size)}
                </ColumnDataCell>
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 0 }}>
                  <Tooltip title="표시명 변경">
                    <IconButton
                      size="small"
                      aria-label="표시명 변경"
                      onClick={(e) => {
                        e.stopPropagation();
                        setRenameTarget({
                          type: 'row',
                          id: ex.id,
                          value: ex.title || (ex.file?.name || '').replace(/\.pdf$/i, '') || '',
                        });
                      }}
                      onMouseDown={(e) => e.stopPropagation()}
                    >
                      <EditOutlined sx={{ width: 14, height: 14 }} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="제거">
                    <IconButton
                      size="small"
                      aria-label="제거"
                      onClick={(e) => {
                        e.stopPropagation();
                        onChange(extras.filter((row) => row.id !== ex.id));
                      }}
                      onMouseDown={(e) => e.stopPropagation()}
                    >
                      <Clear sx={{ width: 14, height: 14 }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              </ColumnRow>
            );
          })
        )}
      </SubMatTemplateSendSection>

      <Menu
        open={Boolean(rowMenu)}
        onClose={() => setRowMenu(null)}
        anchorReference="anchorPosition"
        anchorPosition={rowMenu ? { top: rowMenu.mouseY, left: rowMenu.mouseX } : undefined}
      >
        <MenuItem
          onClick={() => {
            const it = rowMenu?.item;
            setRowMenu(null);
            if (!it) return;
            setRenameTarget({
              type: 'row',
              id: it.id,
              value: it.title || (it.file?.name || '').replace(/\.pdf$/i, '') || '',
            });
          }}
        >
          표시명 변경
        </MenuItem>
        <MenuItem
          sx={{ color: 'error.main' }}
          onClick={() => {
            const id = rowMenu?.item?.id;
            setRowMenu(null);
            if (id) onChange(extras.filter((row) => row.id !== id));
          }}
        >
          제거
        </MenuItem>
      </Menu>

      <Dialog
        open={Boolean(renameTarget)}
        onClose={() => setRenameTarget(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>
          {renameTarget?.type === 'section' ? '섹션 표시명 변경' : '표시명 변경'}
        </DialogTitle>
        <DialogContent>
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
            {renameTarget?.type === 'section'
              ? '목차·합본에만 보이며, 실제 파일과는 무관합니다.'
              : `실제 파일명: ${extras.find((e) => e.id === renameTarget?.id)?.file?.name || '-'} · 목차·합본 표시용`}
          </Typography>
          <TextField
            fullWidth
            size="small"
            label="표시명"
            value={renameTarget?.value || ''}
            onChange={(e) => setRenameTarget((prev) => (prev ? { ...prev, value: e.target.value } : prev))}
            autoFocus
            onKeyDown={(e) => {
              if (e.key === 'Enter') commitRename();
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={commitRename}>저장</Button>
          <Button onClick={() => setRenameTarget(null)}>취소</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SubMatTemplateExtraAttachmentsList;
